import { useEffect, useState, type FormEvent } from 'react'
import { PortalDisclosure } from '@gamecodex/portal-ui'
import { LISTINGS, BROWSER_QUOTES, type Listing } from './catalog'
import { useNegotiate } from './useNegotiate'
import { useAuth } from './useAuth'
import { useCheckout } from './useCheckout'
import { PrivacyPolicy } from './legal/PrivacyPolicy'
import { Terms } from './legal/Terms'

type Stage = 'price' | 'cart' | 'result'

function currentPath() {
  return typeof window !== 'undefined' ? window.location.pathname : '/'
}

/**
 * scratchyo.com
 *
 * Itch-shaped on purpose: a grid of listings, a price slider, an "Add to
 * Cart" button. The PWYW mechanic is real up to the cart — the Suggested
 * Price reacts to your number, but never blocks it — the twist is what comes
 * after: checkout requires a REAL account (see atlasAuth.ts) and then, having
 * asked that much of you, never completes. Not a fake error — the Cart
 * persona (netlify/lib/cart.ts) finds a fresh, in-character reason every time.
 *
 * /privacy and /terms are plain pathname routes (no router lib needed for two
 * static pages); netlify.toml's SPA catch-all serves index.html for both.
 */
export default function App() {
  const [path, setPath] = useState(currentPath)
  const navigate = (to: string) => {
    window.history.pushState({}, '', to)
    setPath(to)
  }
  useEffect(() => {
    const onPopState = () => setPath(currentPath())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const negotiate = useNegotiate()
  const auth = useAuth()
  const checkout = useCheckout()

  const [active, setActive] = useState<Listing | null>(null)
  const [offer, setOffer] = useState(0)
  const [stage, setStage] = useState<Stage>('price')
  const [authMode, setAuthMode] = useState<'register' | 'login'>('register')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const open = (listing: Listing) => {
    setActive(listing)
    setOffer(listing.suggested)
    setStage('price')
    setEmail('')
    setPassword('')
    negotiate.dismiss()
    checkout.dismiss()
  }

  const close = () => {
    setActive(null)
  }

  const addToCart = () => {
    if (!active) return
    void negotiate.claim(active.id, active.title, active.suggested, offer)
  }

  const onAuthSubmit = (e: FormEvent) => {
    e.preventDefault()
    void auth.submit(authMode, email, password)
  }

  const completePurchase = () => {
    if (!active || !auth.session) return
    void checkout.complete(auth.session.accessToken, active.title, offer, auth.session.account.email)
    setStage('result')
  }

  if (path === '/privacy') return <PrivacyPolicy onNavigate={navigate} />
  if (path === '/terms') return <Terms onNavigate={navigate} />

  return (
    <div className="app">
      <header className="top">
        <span className="wordmark">Scratchyo</span>
        <span className="tag">pay what you want</span>
        {auth.session && (
          <span className="signed-in">
            {auth.session.account.email}
            <button type="button" className="linklike" onClick={auth.signOut}>
              sign out
            </button>
          </span>
        )}
      </header>

      <main className="wrap">
        <div className="ticker">
          {BROWSER_QUOTES.map((q) => (
            <span key={q}>{q}</span>
          ))}
        </div>

        <section className="grid">
          {LISTINGS.map((l) => (
            <article key={l.id} className="card" onClick={() => open(l)}>
              <div className="thumb" aria-hidden />
              <h2>{l.title}</h2>
              <div className="meta">
                {l.dev} · {l.tag}
              </div>
              <p>{l.blurb}</p>
              <div className="price">
                {l.suggested > 0 ? `$${l.suggested} suggested` : 'name your price'}
              </div>
            </article>
          ))}
        </section>

        <PortalDisclosure
          portal="Scratchyo"
          detail="Listings are fictional and no payment is ever charged — but the account you create at checkout is a real one."
          className="disclosure"
        />
        <nav className="legalLinks">
          <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy') }}>
            Privacy Policy
          </a>
          <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms') }}>
            Terms &amp; Conditions
          </a>
        </nav>
      </main>

      {active && (
        <div className="modal-backdrop" onClick={close}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{active.title}</h3>
            <p className="meta">{active.dev}</p>

            {stage === 'price' && (
              <>
                <label className="slider-label" htmlFor="offer">
                  Your price: <strong>${offer}</strong>
                </label>
                <input
                  id="offer"
                  type="range"
                  min={0}
                  max={Math.max(active.suggested * 2, 10)}
                  value={offer}
                  onChange={(e) => setOffer(Number(e.target.value))}
                />

                {!negotiate.line && (
                  <div className="row">
                    <button type="button" onClick={addToCart} disabled={negotiate.pending}>
                      {negotiate.pending ? 'Reviewing your offer…' : 'Add to Cart'}
                    </button>
                    <span className="hint">Suggested price: ${active.suggested || 0}</span>
                  </div>
                )}

                {negotiate.line && (
                  <div className="verdict" role="status">
                    <span className="who">The Suggested Price</span>
                    <p>{negotiate.line}</p>
                    <div className="row">
                      <button type="button" onClick={() => setStage('cart')}>
                        Continue to checkout
                      </button>
                      {negotiate.degraded && <span className="hint">Reviewed under standard guidance.</span>}
                    </div>
                  </div>
                )}

                {negotiate.error && (
                  <p className="err" role="alert">
                    {negotiate.error}
                  </p>
                )}
              </>
            )}

            {stage === 'cart' && (
              <>
                <div className="cart-summary">
                  <span>{active.title}</span>
                  <strong>${offer}</strong>
                </div>

                {!auth.session && (
                  <form className="auth" onSubmit={onAuthSubmit}>
                    <p className="auth-hint">
                      {authMode === 'register'
                        ? 'Create an account to check out. This is a real Scratchyo account.'
                        : 'Sign in to check out.'}
                    </p>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <input
                      type="password"
                      required
                      autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                      placeholder="Password (min 8 characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <div className="row">
                      <button type="submit" disabled={auth.pending}>
                        {auth.pending
                          ? 'Working…'
                          : authMode === 'register'
                            ? 'Create account'
                            : 'Sign in'}
                      </button>
                      <button
                        type="button"
                        className="linklike"
                        onClick={() => setAuthMode(authMode === 'register' ? 'login' : 'register')}
                      >
                        {authMode === 'register' ? 'Already have an account?' : 'Need an account?'}
                      </button>
                    </div>
                    {auth.error && (
                      <p className="err" role="alert">
                        {auth.error}
                      </p>
                    )}
                  </form>
                )}

                {auth.session && (
                  <div className="row">
                    <button type="button" onClick={completePurchase}>
                      Complete purchase
                    </button>
                    <span className="hint">as {auth.session.account.email}</span>
                  </div>
                )}
              </>
            )}

            {stage === 'result' && (
              <div className="verdict" role="status">
                <span className="who">Scratchyo Cart</span>
                {checkout.pending && <p>Completing checkout…</p>}
                {checkout.reason && <p>{checkout.reason}</p>}
                {checkout.error && (
                  <p className="err" role="alert">
                    {checkout.error}
                  </p>
                )}
                <div className="row">
                  <button type="button" onClick={close}>
                    Close
                  </button>
                  {checkout.degraded && <span className="hint">Reviewed under standard guidance.</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
